import React from "react";
import { Box, InputBase, Typography } from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { NAV, transition } from "./navTheme";

/** False for modified/middle clicks, which keep the browser's native behaviour. */
export function isPlainClick(event: React.MouseEvent): boolean {
  return !(
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.button !== 0
  );
}

export const IconTile: React.FC<{
  size?: number;
  radius?: number;
  active?: boolean;
  children: React.ReactNode;
}> = ({ size = 30, radius = 9, active = false, children }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: `${radius}px`,
      display: "grid",
      placeItems: "center",
      color: active ? NAV.accent : "inherit",
      backgroundColor: active ? NAV.accentSoft : NAV.hover,
      transition: transition("background-color", "color"),
    }}
  >
    {children}
  </Box>
);

export const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: "inherit",
      fontSize: "0.6875rem",
      fontWeight: 600,
      lineHeight: 1.6,
      color: NAV.textMuted,
      border: `1px solid ${NAV.border}`,
      borderRadius: "6px",
      px: 0.625,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

export const SectionLabel: React.FC<{
  children: React.ReactNode;
  sx?: object;
}> = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: NAV.textMuted,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

/** Page name over its group name, both clipped to one line. */
export const PageLabel: React.FC<{
  title: string;
  caption?: string;
  titleSize?: string;
  bold?: boolean;
}> = ({ title, caption, titleSize = "0.8125rem", bold = false }) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Typography
      sx={{
        fontSize: titleSize,
        fontWeight: bold ? 600 : 550,
        color: "inherit",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </Typography>
    {caption && (
      <Typography sx={{ fontSize: "0.6875rem", color: NAV.textMuted }}>
        {caption}
      </Typography>
    )}
  </Box>
);

export const NoMatch: React.FC<{ query: string; py?: number }> = ({
  query,
  py = 3,
}) => (
  <Typography
    sx={{
      px: 1.5,
      py,
      textAlign: "center",
      fontSize: "0.8125rem",
      color: NAV.textMuted,
    }}
  >
    No page matches “{query.trim()}”.
  </Typography>
);

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  placeholder: string;
  ariaLabel: string;
  height?: number;
  fontSize?: string;
  iconSize?: number;
  radius?: number;
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  trailing?: React.ReactNode;
  /** `flat` drops the sunken box — for the palette, which has its own divider. */
  variant?: "boxed" | "flat";
}

export const SearchField: React.FC<SearchFieldProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder,
  ariaLabel,
  height = 40,
  fontSize = "0.8125rem",
  iconSize = 18,
  radius = 12,
  autoFocus,
  inputRef,
  trailing,
  variant = "boxed",
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      height,
      px: 1.25,
      ...(variant === "boxed" && {
        borderRadius: `${radius}px`,
        backgroundColor: NAV.surfaceSunken,
        border: `1px solid ${NAV.border}`,
        transition: transition("border-color"),
        "&:focus-within": { borderColor: NAV.accentBorder },
      }),
    }}
  >
    <SearchRounded
      sx={{ fontSize: iconSize, color: NAV.textMuted, flexShrink: 0 }}
    />
    <InputBase
      autoFocus={autoFocus}
      inputRef={inputRef}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      inputProps={{ "aria-label": ariaLabel }}
      sx={{
        flex: 1,
        minWidth: 0,
        color: NAV.textActive,
        fontSize,
        "& input::placeholder": { color: NAV.textMuted, opacity: 1 },
      }}
    />
    {trailing}
  </Box>
);
